import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Record, RecordType, Vehicle } from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Plus,
  Edit,
  Trash2,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { RecordForm } from './RecordForm';
import { formatDate } from '../../utils/dateFormat';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface RecordsResponse {
  records: Record[];
  total: number;
  page: number;
  limit: number;
}

export const Records = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Record | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [apiFilters, setApiFilters] = useState({
    vehicleId: '',
    type: '',
    startDate: '',
    endDate: '',
  });

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      if (!user) return [];
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data || [];
    },
    enabled: !!user,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['records', page, limit, apiFilters],
    queryFn: async () => {
      if (!user) return null;
      const token = await user.getIdToken();
      const params: any = { page, limit };
      if (apiFilters.vehicleId) params.vehicleId = apiFilters.vehicleId;
      if (apiFilters.type) params.type = apiFilters.type;
      if (apiFilters.startDate) params.startDate = apiFilters.startDate;
      if (apiFilters.endDate) params.endDate = apiFilters.endDate;

      const response = await axios.get(`${API_URL}/records`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data.data as RecordsResponse;
    },
    enabled: !!user,
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      await axios.delete(`${API_URL}/records/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['records'] });
      toast.success(t('record.deleteSuccess'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('errors.generic'));
    },
  });

  const handleAdd = () => {
    setEditingRecord(null);
    setIsFormOpen(true);
  };

  const handleEdit = (record: Record) => {
    setEditingRecord(record);
    setIsFormOpen(true);
  };

  const handleDelete = async (record: Record) => {
    if (window.confirm(t('record.deleteConfirm'))) {
      await deleteMutation.mutateAsync(record.id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingRecord(null);
  };

  const handleApiFilterChange = (key: string, value: string) => {
    setApiFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setApiFilters({
      vehicleId: '',
      type: '',
      startDate: '',
      endDate: '',
    });
    setSearchQuery('');
    setPage(1);
  };

  const getRecordTypeLabel = (type: RecordType) => {
    return t(`record.types.${type}`);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency: currency === 'UAH' ? 'UAH' : currency,
    }).format(amount);
  };

  const filteredRecords = data?.records.filter((record) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      record.title.toLowerCase().includes(searchLower) ||
      record.description.toLowerCase().includes(searchLower) ||
      record.location?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const isInitialLoading = isLoading && !data;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('record.title')}
        </h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <Filter className="w-5 h-5 mr-2" />
            {t('common.filter')}
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('record.add')}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('common.search')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('vehicle.title')}
              </label>
              <select
                value={apiFilters.vehicleId}
                onChange={(e) =>
                  handleApiFilterChange('vehicleId', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('common.all')}</option>
                {vehicles?.map((vehicle: Vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('record.type')}
              </label>
              <select
                value={apiFilters.type}
                onChange={(e) => handleApiFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('common.all')}</option>
                {(
                  [
                    'maintenance',
                    'repair',
                    'fuel',
                    'parts',
                    'other',
                  ] as RecordType[]
                ).map((type) => (
                  <option key={type} value={type}>
                    {getRecordTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('record.date')} {t('common.from')}
              </label>
              <input
                type="date"
                value={apiFilters.startDate}
                onChange={(e) =>
                  handleApiFilterChange('startDate', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('record.date')} {t('common.to')}
              </label>
              <input
                type="date"
                value={apiFilters.endDate}
                onChange={(e) =>
                  handleApiFilterChange('endDate', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <X className="w-4 h-4 mr-2" />
              {t('common.clearFilters')}
            </button>
          </div>
        </div>
      )}

      {filteredRecords && filteredRecords.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-gray-600 mb-4">{t('record.noRecords')}</p>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('record.add')}
          </button>
        </div>
      ) : (
        <>
          <div
            className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-opacity ${isFetching ? 'opacity-60' : ''}`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.title')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.mileage')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.cost')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('record.location')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecords?.map((record) => {
                    const vehicle = vehicles?.find(
                      (v: Vehicle) => v.id === record.vehicleId
                    );
                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(record.date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{record.title}</div>
                            {record.description && (
                              <div className="text-gray-500 text-xs mt-1 truncate max-w-xs">
                                {record.description}
                              </div>
                            )}
                            {vehicle && (
                              <div className="text-gray-400 text-xs mt-1">
                                {vehicle.name}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                            {getRecordTypeLabel(record.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.mileage.toLocaleString()} {t('vehicle.km')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(record.cost, record.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.location || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(record)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(record)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {t('common.showing')} {(page - 1) * limit + 1} -{' '}
                {Math.min(page * limit, data?.total || 0)} {t('common.of')}{' '}
                {data?.total || 0} {t('common.results')}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isFormOpen && (
        <RecordForm
          record={editingRecord}
          vehicles={vehicles || []}
          onClose={handleFormClose}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['records'] });
            handleFormClose();
          }}
        />
      )}
    </div>
  );
};
